<template>
  <div>
    <dropzone 
      ref="uploader"
      @on-success="uploadSuccess"
      @on-upload-progress="uploadProgress"
      @on-error="uploadError"
      @on-add="uploadAddFile"
      hidden
    />
    <div class="upload-box">
      Drag Files Or Click Here To Upload
      <input type="file" @change="addFile" multiple />
    </div>

    <v-data-iterator
      v-if="picker"
      :items="media"
      :options.sync="options"
      :server-items-length="totalMedia"
      :loading="loading"
      :footer-props="{
        itemsPerPageOptions: [12,24,48,-1]
      }"
    >
      <template v-slot:loading>
        <v-row>
          <v-col class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-col>
        </v-row>
      </template>
      <template v-slot:default="props">
        <v-row class="align-center">
          <v-col
            v-for="item in props.items"
            :key="(item.id ? item.id : '') + '-' + (item.identifier ? item.identifier : '')"
            cols="12"
            sm="6"
            md="3"
            class="pickerSelectCol"
            @click="$emit('on-select',item)"
          >
            <v-img
              v-if="imagePicker && item.id"
              aspect-ratio="1.7778"
              :src="item.url_path"
              contain
            />
            <v-progress-linear
              v-else-if="item.uploading"
              color="light-blue"
              height="10"
              :value="item.progress"
              buffer-value="0"
              stream
            ></v-progress-linear>
          </v-col>
        </v-row>
      </template>
    </v-data-iterator>

    <v-data-table
      v-else

      :headers="headers"
      :items="media"
      :loading="loading"

      :options.sync="options"
      :server-items-length="totalMedia"

      :footer-props="{
        itemsPerPageOptions: [12,24,48,-1]
      }"
    >
      <template v-slot:item.type="{item}">
        <div>
          <div v-if="item.uploading && !item.hasError">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </div>
          <v-tooltip top v-else-if="item.hasError">
            <template v-slot:activator="{on}">
              <v-icon v-on="on" class="error--text">mdi-alert-circle</v-icon>
            </template>
            <span v-html="item.hasError"></span>
          </v-tooltip>
          <v-tooltip top v-else-if="item.id">
            <template v-slot:activator="{on}">
              <v-icon v-on="on" v-text="getFileIcon(item.type)"></v-icon>
            </template>
            <span>{{ item.type.charAt(0) + item.type.toLowerCase().slice(1) }}</span>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.created_at="{item}">
        <div>
          <div v-if="item.uploading && !item.hasError">
            <v-progress-linear
              color="light-blue"
              height="10"
              :value="item.progress"
              buffer-value="0"
              stream
            ></v-progress-linear>
          </div>
          <div v-else-if="item.id">
            <span>{{ $moment.unix(item.created_at).fromNow() }}</span><span v-if="item.user" class="ml-2 grey--text caption">By {{item.user.full_name}}</span>
          </div>
        </div>
      </template>
      <template v-slot:item.name="{item}">
        <span class="mr-2" :class="{'error--text':item.hasError}">{{ item.name }}</span><span class="grey--text caption">({{ item.size | filesize }})</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import Dropzone from '@/components/Dropzone'

export default {
  name: 'MediaBrowser',
  props: {
    imagePicker: Boolean,
    videoPicker: Boolean,
    filterType: String
  },
  components: {
    'dropzone': Dropzone
  },
  data: () => ({
    loading: true,
    headers: [
      {
        text: 'ID',
        align: 'center',
        sortable: false,
        value: 'id',
        width: '50px'
      },
      {
        text: '',
        sortable: false,
        width: '50px',
        value: 'type'
      },
      {
        text: 'Name',
        align: 'start',
        value: 'name',
      },
      {
        text: 'Uploaded',
        align: 'start',
        value: 'created_at'
      }
    ],
    media: [],
    options: {
      itemsPerPage: 12,
      sortBy: [
        'created_at'
      ],
      sortDesc: [
        true
      ]
    },
    totalMedia: 0
  }),
  mounted: function() {
    this.getDataFromApi();
  },
  watch: {
    options: {
      handler: function() {
        this.getDataFromApi();
      },
      deep: true
    }
  },
  computed: {
    picker: function() {
      return (this.imagePicker || this.videoPicker);
    }
  },
  methods: {
    getFileIcon: function(type) {
      switch(type) {
        case 'IMAGE':
          return 'mdi-image';
        case 'VIDEO':
          return 'mdi-video';
        case 'PDF':
          return 'mdi-file-pdf';
        default: 
          return 'mdi-file';
      }
    },
    getDataFromApi: function() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      let qParams = [];

      this.loading = true;
      qParams.push('expand=user');
      qParams.push('sort=' + (sortDesc[0] ? '-' : '') + sortBy[0]);
      qParams.push('per-page=' + itemsPerPage);
      qParams.push('page=' + page);

      if(this.imagePicker) {
        qParams.push('f[type]=IMAGE');
      } else if(this.videoPicker) {
        qParams.push('f[type]=VIDEO');
      } else if(this.filterType) {
        qParams.push('f[type][in]=' + this.filterType);
      }
      
      this.$http.get('media?' + qParams.join('&'))
        .then(response => {
          this.media = response.data.items;
          this.totalMedia = response.data._meta.totalCount;
        })
        .catch(err => {
          if(err.response.status == 401) return;

          this.$swal.fire('Error', 'There was an error loading the media', 'error');
        })
        .finally(() => {
          this.loading = false;
        })
    },

    updateFile: function(file,progress,data) {
      let index = null;
      for(let i = 0; i < this.media.length; i++) {
        if(file.identifier && this.media[i].identifier == file.identifier) index = i; 
      }
      let item = {};

      if(data) {
        if(index !== null) {
          this.$set(this.media, index, data);
        } else {
          this.media.unshift(data);
          this.totalMedia++;
        }
      } else {
        if(!progress) progress = 0;
        if(index !== null) {
          this.$set(this.media[index], 'progress', progress);
          if(file.hasError) {
            this.$set(this.media[index], 'hasError', file.hasError);
          }
        } else {
          item = {
            name: file.name,
            size: file.size,
            identifier: file.identifier,
            uploading: true,
            progress: progress
          }
          this.media.unshift(item);
          this.totalMedia++;
        }
      }
    },


    addFile(e) {
      let fileList = null;
      if(e.dataTransfer && e.dataTransfer.files) {
        fileList = e.dataTransfer.files;
      } else if(e.srcElement && e.srcElement.files) {
        fileList = e.srcElement.files;
      }
      if(!fileList) return;
      
      for(let i = 0; i < fileList.length; i++) {
        let file = fileList[i];
        this.$refs.uploader.addFile(file, 0);
      }
    },
    uploadSuccess(d) {
      this.updateFile(d.file, 100, d.response);
    },

    uploadAddFile(d) {
      this.updateFile(d);
    },

    uploadError(d) {
      let file = d.file;
      file.hasError = d.message;
      this.updateFile(file);
    },

    uploadProgress(d) {
      this.updateFile(d.file, d.progress);
    },
  }
}
</script>

<style lang="scss" scoped>
  .pickerSelectCol {
    cursor: pointer;
    &:hover {
      background-color: #F0F0F0;
    }
  }

  .upload-box {
    max-width: 400px;
    margin: 10px auto;
    padding: 30px;
    background-color: #F0F0F0;
    border: 1px solid #CFCFCF;
    position: relative;
    text-align: center;
    input {
      position: absolute;
      top:0; left: 0; right: 0; bottom: 0;
      opacity: 0;
      cursor: pointer;
    }
  }

</style>